import React, { useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { AccountType, AccountSearchParams } from '../types/account';

interface AccountSearchBarProps {
  onSearch: (params: AccountSearchParams) => void;
  showFilters?: boolean;
}

const AccountSearchBar: React.FC<AccountSearchBarProps> = ({
  onSearch,
  showFilters = true,
}) => {
  const [searchText, setSearchText] = useState('');
  const [accountType, setAccountType] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [isActive, setIsActive] = useState<string>('');

  const handleSearch = () => {
    const params: AccountSearchParams = {
      search: searchText || undefined,
      accountType: accountType ? (accountType as AccountType) : undefined,
      currency: currency || undefined,
      isActive: isActive ? isActive === 'true' : undefined,
    };
    onSearch(params);
  };

  const handleClear = () => {
    setSearchText('');
    setAccountType('');
    setCurrency('');
    setIsActive('');
    onSearch({});
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Search by account number, name, or partner..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {showFilters && (
          <>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={accountType}
                  label="Account Type"
                  onChange={(e) => setAccountType(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value={AccountType.MERCHANT_ACCOUNT}>Merchant</MenuItem>
                  <MenuItem value={AccountType.RETAILER_ACCOUNT}>Retailer</MenuItem>
                  <MenuItem value={AccountType.AGENT_ACCOUNT}>Agent</MenuItem>
                  <MenuItem value={AccountType.VOUCHER_CLEARING}>Voucher Clearing</MenuItem>
                  <MenuItem value={AccountType.COMMISSION_REVENUE}>Commission Revenue</MenuItem>
                  <MenuItem value={AccountType.MERCHANT_PAYABLE}>Merchant Payable</MenuItem>
                  <MenuItem value={AccountType.RETAILER_RECEIVABLE}>Retailer Receivable</MenuItem>
                  <MenuItem value={AccountType.AGENT_POOL_USD}>Agent Pool (USD)</MenuItem>
                  <MenuItem value={AccountType.AGENT_POOL_ZWG}>Agent Pool (ZWG)</MenuItem>
                  <MenuItem value={AccountType.AGENT_POOL_ZAR}>Agent Pool (ZAR)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={currency}
                  label="Currency"
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <MenuItem value="">All Currencies</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="ZWG">ZWG</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={isActive}
                  label="Status"
                  onChange={(e) => setIsActive(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex" gap={1}>
            <Button variant="contained" fullWidth onClick={handleSearch}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleClear} sx={{ minWidth: 'auto' }}>
              <ClearIcon />
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountSearchBar;
